'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/use-toast'
import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from 'react-i18next'

const formSchema = z.object({
  title: z.string().min(1, {
    message: 'Title is required'
  }),
  description: z.string().nullable(),
  recurrency_value: z.number().int().min(1, {
    message: 'Must be at least 1'
  }),
  recurrency_unit: z.enum(['days', 'weeks', 'months', 'years']),
  priority: z.enum(['low', 'medium', 'high']),
  status: z.enum(['active', 'paused', 'completed']).default('active'),
  assigned_to: z.string().uuid().nullable(),
  next_trigger_at: z.date(),
  onetime: z.boolean().default(false),
})

type FormValues = z.infer<typeof formSchema>

interface CreateReminderFormProps {
  assetId?: number
  onSuccess?: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  initialData?: any
}

export function CreateReminderForm({ assetId, onSuccess, onCancel, mode = 'create', initialData }: CreateReminderFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()
  const { t } = useTranslation()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || null,
      recurrency_value: initialData?.recurrency_value || 1,
      recurrency_unit: initialData?.recurrency_unit || 'days',
      priority: initialData?.priority || 'medium',
      status: initialData?.status || 'active',
      assigned_to: initialData?.assigned_to || null,
      next_trigger_at: initialData?.next_trigger_at ? new Date(initialData.next_trigger_at) : new Date(),
      onetime: initialData?.onetime || false,
    },
  })

  const createReminder = async (params: FormValues) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const now = new Date().toISOString()

      const { data, error } = await supabase
        .from('reminders')
        .insert([
          {
            asset_id: assetId || null,
            user_id: user.id,
            title: params.title,
            description: params.description,
            recurrency_value: params.recurrency_value,
            recurrency_unit: params.recurrency_unit,
            priority: params.priority,
            status: params.status,
            assigned_to: params.assigned_to,
            next_trigger_at: params.next_trigger_at.toISOString(),
            last_triggered_at: null,
            created_at: now,
            updated_at: now,
            onetime: params.onetime
          }
        ])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating reminder:', error)
      throw error
    }
  }

  const updateReminder = async (params: FormValues) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const now = new Date().toISOString()

      const { data, error } = await supabase
        .from('reminders')
        .update({
          title: params.title,
          description: params.description,
          recurrency_value: params.recurrency_value,
          recurrency_unit: params.recurrency_unit,
          priority: params.priority,
          status: params.status,
          assigned_to: params.assigned_to,
          next_trigger_at: params.next_trigger_at.toISOString(),
          updated_at: now,
          onetime: params.onetime
        })
        .eq('id', initialData.id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating reminder:', error)
      throw error
    }
  }

  async function onSubmit(data: FormValues) {
    try {
      setIsLoading(true)
      if (mode === 'edit') {
        await updateReminder(data)
        toast({
          title: t('common.success'),
          description: t('reminders.updateSuccess'),
        })
      } else {
        await createReminder(data)
        toast({
          title: t('common.success'),
          description: t('reminders.createSuccess'),
        })
      }
      onSuccess?.()
    } catch (error) {
      toast({
        title: t('common.error'),
        description: mode === 'edit' ? t('reminders.updateError') : t('reminders.createError'),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('reminders.form.title')}</FormLabel>
              <FormControl>
                <Input placeholder={t('reminders.form.titlePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('reminders.form.description')}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t('reminders.form.descriptionPlaceholder')}
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="onetime"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {t('reminders.form.onetime')}
                </FormLabel>
                <FormDescription>
                  {t('reminders.form.onetimeDescription')}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="data-[state=checked]:bg-blue-500 data-[state=unchecked]:bg-gray-200"
                />
              </FormControl>
            </FormItem>
          )}
        />

        {!form.watch('onetime') && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="recurrency_value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('reminders.form.recurrencyValue')}</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min={1} 
                      {...field} 
                      onChange={e => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recurrency_unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('reminders.form.recurrencyUnit')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('reminders.form.selectUnit')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="days">{t('forms.reminder.units.days')}</SelectItem>
                      <SelectItem value="weeks">{t('forms.reminder.units.weeks')}</SelectItem>
                      <SelectItem value="months">{t('forms.reminder.units.months')}</SelectItem>
                      <SelectItem value="years">{t('forms.reminder.units.years')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('reminders.form.priority')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('reminders.form.selectPriority')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="low">{t('reminders.priority.low')}</SelectItem>
                    <SelectItem value="medium">{t('reminders.priority.medium')}</SelectItem>
                    <SelectItem value="high">{t('reminders.priority.high')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('reminders.form.status')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('reminders.form.selectStatus')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">{t('reminders.status.active')}</SelectItem>
                    <SelectItem value="paused">{t('reminders.status.paused')}</SelectItem>
                    <SelectItem value="completed">{t('reminders.status.completed')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="next_trigger_at"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('reminders.form.nextTrigger')}</FormLabel>
              <FormControl>
                <Input 
                  type="datetime-local" 
                  {...field} 
                  value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ''}
                  onChange={e => field.onChange(new Date(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading 
              ? (mode === 'edit' ? t('common.updating') : t('common.creating'))
              : (mode === 'edit' ? t('common.update') : t('common.create'))}
          </Button>
        </div>
      </form>
    </Form>
  )
} 
